import type { PosShortcutAction, PosShortcutBindings } from '@/store/posUiStore';
import { DEFAULT_SHORTCUTS } from '@/store/posUiStore';
import i18n from '@/i18n';

export type ShortcutContext = 'global' | 'cart' | 'payment';

export interface PosShortcutDefinition {
  action: PosShortcutAction;
  labelKey: string;
  context: ShortcutContext;
}

export const POS_SHORTCUT_DEFINITIONS: PosShortcutDefinition[] = [
  { action: 'showShortcuts', labelKey: 'posShortcuts.showShortcuts', context: 'global' },
  { action: 'focusSearch', labelKey: 'posShortcuts.focusSearch', context: 'global' },
  { action: 'focusBarcode', labelKey: 'posShortcuts.focusBarcode', context: 'global' },
  { action: 'toggleFavorites', labelKey: 'posShortcuts.toggleFavorites', context: 'global' },
  { action: 'openPay', labelKey: 'posShortcuts.openPay', context: 'global' },
  { action: 'openTodaySales', labelKey: 'posShortcuts.openTodaySales', context: 'global' },
  { action: 'openPending', labelKey: 'posShortcuts.openPending', context: 'global' },
  { action: 'holdCart', labelKey: 'posShortcuts.holdCart', context: 'global' },
  { action: 'toggleHeldCarts', labelKey: 'posShortcuts.toggleHeldCarts', context: 'global' },
  { action: 'openExpense', labelKey: 'posShortcuts.openExpense', context: 'global' },
  { action: 'openReturn', labelKey: 'posShortcuts.openReturn', context: 'global' },
  { action: 'closeModal', labelKey: 'posShortcuts.closeModal', context: 'global' },
  { action: 'removeCartLine', labelKey: 'posShortcuts.removeCartLine', context: 'cart' },
  { action: 'prevCartLine', labelKey: 'posShortcuts.prevCartLine', context: 'cart' },
  { action: 'nextCartLine', labelKey: 'posShortcuts.nextCartLine', context: 'cart' },
  { action: 'increaseCartQty', labelKey: 'posShortcuts.increaseCartQty', context: 'cart' },
  { action: 'decreaseCartQty', labelKey: 'posShortcuts.decreaseCartQty', context: 'cart' },
  { action: 'cyclePaymentMode', labelKey: 'posShortcuts.cyclePaymentMode', context: 'payment' },
  { action: 'paymentModeCash', labelKey: 'posShortcuts.paymentModeCash', context: 'payment' },
  { action: 'paymentModeBank', labelKey: 'posShortcuts.paymentModeBank', context: 'payment' },
  { action: 'paymentModeBoth', labelKey: 'posShortcuts.paymentModeBoth', context: 'payment' },
  { action: 'completeSale', labelKey: 'posShortcuts.completeSale', context: 'payment' },
];

export function formatShortcutKey(binding: string): string {
  return binding
    .split('+')
    .map((part) => {
      const p = part.trim();
      if (p === 'control' || p === 'ctrl') return 'Ctrl';
      if (p === 'shift') return 'Shift';
      if (p === 'alt') return 'Alt';
      if (p === 'escape') return 'Esc';
      if (p.toLowerCase() === 'delete') return 'Del';
      if (p.toLowerCase() === 'arrowup') return '↑';
      if (p.toLowerCase() === 'arrowdown') return '↓';
      if (/^f([1-9]|1[0-2])$/i.test(p)) return p.toUpperCase();
      if (p.length === 1) return p.toUpperCase();
      return p;
    })
    .join(' + ');
}

function bindingKeyToCode(key: string): string | null {
  const k = key.toLowerCase();
  if (/^f([1-9]|1[0-2])$/.test(k)) return k.toUpperCase();
  if (k === 'delete') return 'Delete';
  if (k === 'arrowup') return 'ArrowUp';
  if (k === 'arrowdown') return 'ArrowDown';
  if (key === '=') return 'Equal';
  if (key === '-') return 'Minus';
  if (key.length === 1) {
    if (key >= 'a' && key <= 'z') return `Key${key.toUpperCase()}`;
    if (key >= '0' && key <= '9') return `Digit${key}`;
  }
  return null;
}

function matchSpecialKey(event: Pick<KeyboardEvent, 'code' | 'key'>, key: string, needsCtrl: boolean, needsAlt: boolean) {
  const k = key.toLowerCase();
  if (k === 'delete') return event.code === 'Delete';
  if (key === '=') {
    return event.code === 'Equal' || event.code === 'NumpadAdd' || (event.key === '+' && !needsAlt && !needsCtrl);
  }
  if (key === '-') {
    return event.code === 'Minus' || event.code === 'NumpadSubtract';
  }
  return false;
}

/** Match a keyboard event against a binding string (e.g. F8, Alt+h, Control+Enter). */
export function matchPosShortcut(event: Pick<KeyboardEvent, 'code' | 'key' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'altKey'>, binding: string) {
  const parts = binding.toLowerCase().split('+').map((p) => p.trim());
  const key = parts.pop() || '';
  const needsCtrl = parts.includes('control') || parts.includes('ctrl');
  const needsShift = parts.includes('shift');
  const needsAlt = parts.includes('alt');
  const hasCtrl = event.ctrlKey || event.metaKey;

  if (needsCtrl !== hasCtrl) return false;
  if (needsShift !== event.shiftKey) return false;
  if (needsAlt !== event.altKey) return false;

  if (key === 'escape') return event.key === 'Escape' || event.code === 'Escape';
  if (key === 'enter') return event.key === 'Enter' || event.code === 'Enter';

  if (matchSpecialKey(event, key, needsCtrl, needsAlt)) return true;

  const code = bindingKeyToCode(key);
  if (code && event.code === code) return true;

  return event.key.toLowerCase() === key;
}

export function getShortcutRows(bindings: PosShortcutBindings = DEFAULT_SHORTCUTS) {
  return POS_SHORTCUT_DEFINITIONS.map((def) => ({
    ...def,
    label: i18n.t(def.labelKey),
    keys: formatShortcutKey(bindings[def.action] || DEFAULT_SHORTCUTS[def.action]),
  }));
}
